import { CSSProperties, MouseEventHandler, ReactNode } from 'react'

import { Breakpoints } from '@hhgtech/hhg-components'
import { Text } from '@hhgtech/hhg-components/mantine'
import {
  ActionIconProps,
  AspectRatio,
  clsx,
  rem,
  Skeleton,
} from '@mantine/core'

import BookmarkSavedSvg from 'components/icons/BookmarkSavedSvg'
import BookmarkSvg from 'components/icons/BookmarkSvg'
import { DataAttributeKey } from 'interfaces/types'

import {
  StyledCardArticle,
  StyledCardArticleAuthor,
  StyledCardArticleBookmark,
  StyledCardArticleCategory,
  StyledCardArticleContent,
  StyledCardArticleExcerpt,
  StyledCardArticleFooter,
  StyledCardArticleImageWrap,
  StyledCardArticleTitle,
  StyledCardArticleTitleLink,
} from './CardArticle.styled'
import { CardArticleAuthorItem } from './CardArticleAuthor'

export type CardArticleCategory = {
  name: string
  permalink: string
  textColor?: string
}

export type CardArticleAuthor = {
  name: ReactNode
  avatar: string
  permalink: string
  indicator?: string | JSX.Element
}

export type CardArticleItem = {
  ID: number
  permalink: string
  image: string | JSX.Element
  image1x1?: string
  title: string
  excerpt?: string
  category?: CardArticleCategory
  author?: CardArticleAuthorItem
}

export type CardArticleTrackingItem = {
  [DataAttributeKey: DataAttributeKey]: string | number
}

export type CardArticleTrackingProps = Partial<
  Record<
    'image' | 'title' | 'category' | 'author' | 'authorAvatar' | 'bookmark',
    CardArticleTrackingItem
  >
>

export type CardArticleProps = {
  loading?: boolean
  item?: CardArticleItem
  itemTracking?: CardArticleTrackingProps
  type?: 'horizontal' | 'horizontalFooter' | 'vertical'
  showExcerpt?: boolean
  isBookmark?: boolean
  bookmarkButtonProps?: Omit<ActionIconProps, 'className' | 'onClick'>
  onClickBookmark?: MouseEventHandler<HTMLButtonElement>
  className?: string
  style?: CSSProperties
}

export const CardArticle = (props: CardArticleProps) => {
  const {
    loading,
    item,
    itemTracking,
    showExcerpt = false,
    isBookmark,
    bookmarkButtonProps,
    onClickBookmark,
    type = 'vertical',
    className,
    style,
  } = props
  const { permalink, image, image1x1, title, excerpt, category, author } =
    item || {}

  if (loading) {
    return (
      <StyledCardArticle
        className={clsx(className, 'cardArticle', `cardArticle__${type}`)}
        style={style}
      >
        <StyledCardArticleImageWrap className="cardArticleImageWrap">
          <AspectRatio ratio={1.5} className="cardArticleImageRatio">
            <Skeleton width="100%" height="100%" />
          </AspectRatio>
        </StyledCardArticleImageWrap>
        <StyledCardArticleContent className="cardArticleContent">
          <StyledCardArticleCategory className="cardArticleCategory">
            <Skeleton
              width="45%"
              height="1rem"
              className="cardArticleCategoryLink"
            />
          </StyledCardArticleCategory>
          <StyledCardArticleTitleLink className="cardArticleTitleLink">
            <Skeleton width="100%" height="2rem" className="cardArticleTitle" />
          </StyledCardArticleTitleLink>
          {showExcerpt && (
            <StyledCardArticleExcerpt className="cardArticleExcerpt">
              <Skeleton width="100%" height="2rem" />
            </StyledCardArticleExcerpt>
          )}
        </StyledCardArticleContent>
        <StyledCardArticleFooter className="cardArticleFooter">
          <StyledCardArticleAuthor loading={loading} />
          <Skeleton
            width={rem(24)}
            height={rem(24)}
            className="cardArticleBookmark"
          />
        </StyledCardArticleFooter>
      </StyledCardArticle>
    )
  }

  if (!item) {
    return null
  }

  return (
    <StyledCardArticle
      className={clsx(className, 'cardArticle', `cardArticle__${type}`)}
      style={style}
    >
      <StyledCardArticleImageWrap className="cardArticleImageWrap">
        <a href={permalink} {...itemTracking?.image}>
          <AspectRatio ratio={1.5} className="cardArticleImageRatio">
            {typeof image === 'string' ? (
              type === 'horizontalFooter' ? (
                <img
                  alt=""
                  loading="lazy"
                  src={`${image1x1}?w=320&q=85`}
                  style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                />
              ) : type === 'horizontal' ? (
                <picture>
                  <source
                    srcSet={`${image}?w=828&q=85`}
                    media={`(min-width: ${Breakpoints.BREAK_POINT_TABLET}px)`}
                  />
                  <img
                    alt=""
                    loading="lazy"
                    src={`${image1x1}?w=320&q=85`}
                    style={{
                      objectFit: 'cover',
                      width: '100%',
                      height: '100%',
                    }}
                  />
                </picture>
              ) : (
                <img
                  loading="lazy"
                  src={image}
                  sizes="100vw"
                  srcSet={`${image}?w=640&q=75 640w, ${image}?w=750&q=75 750w, ${image}?w=828&q=75 828w, ${image}?w=1080&q=75 1080w, ${image}?w=1200&q=75 1200w, ${image}?w=1920&q=75 1920w, ${image}?w=2048&q=75 2048w, ${image}?w=3840&q=75 3840w`}
                  alt={title}
                  className="cardArticleImage"
                />
              )
            ) : (
              <>{image}</>
            )}
          </AspectRatio>
        </a>
      </StyledCardArticleImageWrap>
      <StyledCardArticleContent className="cardArticleContent">
        <StyledCardArticleCategory
          size="p2"
          as="a"
          className="cardArticleCategory"
          href={category?.permalink}
          style={{ color: category?.textColor }}
          {...itemTracking?.category}
        >
          {category?.name}
        </StyledCardArticleCategory>
        <StyledCardArticleTitleLink
          href={permalink}
          className="cardArticleTitleLink"
          {...itemTracking?.title}
        >
          <StyledCardArticleTitle
            weight="semiBold"
            as="h4"
            size="title1"
            className="cardArticleTitle"
          >
            {title}
          </StyledCardArticleTitle>
        </StyledCardArticleTitleLink>
        {showExcerpt && excerpt && (
          <Text
            size="p2"
            as="div"
            lineClamp={2}
            dangerouslySetInnerHTML={{
              __html: excerpt,
            }}
            sx={{ marginTop: 16 }}
          />
        )}
      </StyledCardArticleContent>
      <StyledCardArticleFooter className="cardArticleFooter">
        <StyledCardArticleAuthor
          author={author}
          authorTracking={{ authorAvatar: itemTracking?.authorAvatar }}
        />
        <StyledCardArticleBookmark
          onClick={onClickBookmark}
          variant="transparent"
          {...bookmarkButtonProps}
          {...itemTracking?.bookmark}
          className={clsx('cardArticleBookmark', { saved: isBookmark })}
        >
          {isBookmark ? (
            <BookmarkSavedSvg className="cardArticleBookmarkIcon--saved" />
          ) : (
            <BookmarkSvg className="cardArticleBookmarkIcon" />
          )}
        </StyledCardArticleBookmark>
      </StyledCardArticleFooter>
    </StyledCardArticle>
  )
}
